import { AnalyticsPipelineEvent, TEventCategory } from '@xero/beanie-types';
import { interactionEventPayload } from '../payloads';
import { BEANIE } from '../constants';
import { IUser } from '../../../models';

class BeanieService {
    private client?: XERO['beanie'];

    /**
     * Sends the sendOutboundConnectionStarted event with a BEANiE payload object
     * @param {string} integrationType - the constant of integration type
     * @param {string} tenantId - the Xero organisation id the connection has been started for
     * @param {IUser} user - the Xero user if logged in
     */
    public sendOutboundConnectionStartedEvent = async (integrationType: string, tenantId: string, user?: IUser) => {
        await this.sendEvent(
            BEANIE.contractIds.connectionStarted,
            'started',
            'interactionEvent',
            'outboundConnectionStarted',
            {
                outboundConnectionName: integrationType.toLowerCase(),
            },
            `apps.xero${integrationType.replace(/(\w)(\w*)/g, (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase())}`,
            BEANIE.outboundConnectionEntityType,
            user,
            tenantId
        );
    };

    /**
     * Sends the sendOutboundConnectionCreated event with a BEANiE payload object
     * @param {string} integrationType - the constant of integration type
     * @param {string} tenantId - the Xero organisation id the connection has been created for
     * @param {IUser} user - the Xero user if logged in
     */
    public sendOutboundConnectionCreatedEvent = async (integrationType: string, tenantId: string, user?: IUser) => {
        await this.sendEvent(
            BEANIE.contractIds.connectionCreated,
            'started',
            'userOutcomeEvent',
            'outboundConnectionCreated',
            {
                outboundConnectionName: integrationType.toLowerCase(),
            },
            `apps.xero${integrationType.replace(/(\w)(\w*)/g, (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase())}`,
            BEANIE.outboundConnectionEntityType,
            user,
            tenantId
        );
    };

    /**
     * Sends the outboundSettingsStepUpdated accounts event with a BEANiE payload object
     * @param {string} integrationType - the constant of integration type
     * @param {string} stepName - the update step name
     * @param {string} tenantId - the Xero organisation id the settings have been updated for
     * @param {IUser} user - the Xero user if logged in
     * @param {string[]} updatedFields - an array with all the updates fields.
     */
    public sendOutboundSettingsStepUpdatedEvent = async (
        integrationType: string,
        stepName: string,
        tenantId: string,
        user?: IUser,
        updatedFields?: string[]
    ) => {
        await this.sendEvent(
            BEANIE.contractIds.settingsStepUpdated,
            'stepUpdated',
            'userOutcomeEvent',
            'outboundSettingsStepUpdated',
            {
                outboundConnectionName: integrationType.toLowerCase(),
                outboundSettingsStepName: stepName,
                settingFieldsExisting: [
                    'paymentGatewayDetails',
                    'salesAccountId',
                    'discountsAccountId',
                    'shippingAccountId',
                    'refundsAccountId',
                    'feesAccountId',
                    'taxCollectedAccountId',
                    'giftCardsAccountId',
                    'taxRate',
                    'shopifyRegion',
                    'dateRange',
                ],
                settingFieldsUpdated: updatedFields,
            },
            `apps.xero${integrationType.replace(
                /(\w)(\w*)/g,
                (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase()
            )}.settings`,
            BEANIE.outboundConnectionSettingsEntityType,
            user,
            tenantId
        );
    };

    /**
     * Sends the outboundConnectionCompleted event with a BEANiE payload object
     * @param {string} integrationType - the constant of integration type
     * @param {string} tenantId - the Xero organisation id the connection has been completed for
     * @param {IUser} user - the Xero user if logged in
     */
    public sendOutboundConnectionCompletedEvent = async (integrationType: string, tenantId: string, user?: IUser) => {
        await this.sendEvent(
            BEANIE.contractIds.connectionCompleted,
            'completed',
            'userOutcomeEvent',
            'outboundConnectionCompleted',
            {
                outboundConnectionName: integrationType.toLowerCase(),
            },
            `apps.xero${integrationType.replace(/(\w)(\w*)/g, (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase())}`,
            BEANIE.outboundConnectionEntityType,
            user,
            tenantId
        );
    };

    /**
     * Sends the outboundConnectionDisconnected event with a BEANiE payload object
     * @param {string} integrationType - the constant of integration type
     * @param {string} tenantId - the Xero organisation id which the connection has been abandoned
     * @param {IUser} user - the Xero user if logged in
     */
    public sendOutboundConnectionAbandonedEvent = async (integrationType: string, tenantId: string, user?: IUser) => {
        await this.sendEvent(
            BEANIE.contractIds.connectionAbandoned,
            'disconnected',
            'interactionEvent',
            'outboundConnectionAbandoned',
            {
                outboundConnectionName: integrationType.toLowerCase(),
            },
            `apps.xero${integrationType.replace(/(\w)(\w*)/g, (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase())}`,
            BEANIE.outboundConnectionEntityType,
            user,
            tenantId
        );
    };

    /**
     * Sends the outboundConnectionDisconnected event with a BEANiE payload object
     * @param {string} integrationType - the constant of integration type
     * @param {string} tenantId - the Xero organisation id the connection has been disconnected for
     * @param {IUser} user - the Xero user if logged in
     */
    public sendOutboundConnectionDisconnectedEvent = async (
        integrationType: string,
        tenantId: string,
        user?: IUser
    ) => {
        await this.sendEvent(
            BEANIE.contractIds.connectionDisconnected,
            'disconnected',
            'interactionEvent',
            'outboundConnectionDisconnected',
            {
                outboundConnectionName: integrationType.toLowerCase(),
            },
            `apps.xero${integrationType.replace(/(\w)(\w*)/g, (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase())}`,
            BEANIE.outboundConnectionEntityType,
            user,
            tenantId
        );
    };

    /**
     * Sends the outboundConnectionOnboardingAccountTax event with a BEANiE payload object
     * @param {string} integrationType - the constant of integration type
     * @param {string} outboundSettingsAccountAndTaxSetupResult - The account and tax page load / interaction result
     * @param {string} tenantId - the Xero organisation id
     * @param {IUser} user - the Xero user if logged in
     */
    public sendOutboundConnectionOnboardingAccountTaxEvent = async (
        integrationType: string,
        outboundSettingsAccountAndTaxSetupResult: string,
        tenantId: string,
        user?: IUser
    ) => {
        await this.sendEvent(
            BEANIE.contractIds.settingsStepUpdated,
            'stepUpdated',
            'interactionEvent',
            'outboundConnectionOnboardingAccountTax',
            {
                outboundConnectionName: integrationType.toLowerCase(),
                outboundSettingsStepName: 'account setup',
                outboundSettingsAccountAndTaxSetup: outboundSettingsAccountAndTaxSetupResult,
            },
            `apps.xero${integrationType.replace(
                /(\w)(\w*)/g,
                (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase()
            )}.settings`,
            BEANIE.outboundConnectionSettingsEntityType,
            user,
            tenantId
        );
    };

    private sendEvent = async (
        contractId: string,
        action: string,
        eventCategory: TEventCategory,
        eventName: string,
        eventProperties: Record<string, unknown>,
        eventSource: string,
        entityType: string,
        user?: IUser,
        tenantId?: string
    ) => {
        const beanieClient = await this.getClient();

        if (!beanieClient) {
            return;
        }

        const payload: AnalyticsPipelineEvent = interactionEventPayload(
            contractId,
            action,
            eventCategory,
            eventName,
            eventProperties,
            eventSource,
            entityType,
            user?.xero_userid,
            tenantId
        );

        beanieClient.sendEvent(payload);
    };

    private getClient = async () => {
        if (!this.client) {
            this.client = await XERO['beanie'];
        }

        return this.client;
    };
}

export const beanieService = new BeanieService();
